import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import CaseContextHeader from "../components/CaseContextHeader";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import ReferencePickList from "../components/ReferencePickList";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Sparkles, BookOpen, FileText, Hammer, Loader2 } from "lucide-react";
import { ENGINE_LABEL, DISPLAY_MODEL } from "../lib/brand";

const MODES = [
  "Full Order Draft", "Facts Section Only", "Submission Summary Only",
  "Observations Draft", "Final Operative Clause Draft",
];

export default function DraftGenerator() {
  const params = useParams();
  const navigate = useNavigate();
  const [caseId, setCaseId] = useState(params.caseId || "");
  const [cases, setCases] = useState([]);
  const [bundle, setBundle] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [grs, setGrs] = useState([]);
  const [citations, setCitations] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [settings, setSettings] = useState({ available_models: [], default_llm_model: "" });

  const [picked, setPicked] = useState({
    template_id: "", gr_ids: [], citation_ids: [], clause_ids: [],
    mode: "Full Order Draft", model: "", notes: "",
  });
  const [generating, setGenerating] = useState(false);
  const [templateSections, setTemplateSections] = useState([]);

  useEffect(() => {
    if (!caseId) {
      api.get("/cases").then((r) => setCases(r.data));
      return;
    }
    api.get(`/cases/${caseId}`).then((r) => setBundle(r.data));
    api.get("/knowledge/templates").then((r) => {
      setTemplates(r.data);
      setPicked((p) => (
        p.template_id ? p : { ...p, template_id: r.data[0]?.template_id || "" }
      ));
    });
    api.get("/knowledge/grs").then((r) => setGrs(r.data));
    api.get("/knowledge/citations").then((r) => setCitations(r.data));
    api.get("/knowledge/clauses").then((r) => setClauses(r.data));
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setPicked((p) => ({ ...p, model: r.data.default_llm_model || "" }));
    });
  }, [caseId]);

  useEffect(() => {
    if (!picked.template_id) {
      setTemplateSections([]);
      return;
    }
    api.get(`/knowledge/templates/${picked.template_id}/sections`)
      .then((r) => setTemplateSections(r.data))
      .catch(() => setTemplateSections([]));
  }, [picked.template_id]);

  const toggleArr = (key, val) => setPicked((p) => {
    const arr = p[key];
    return { ...p, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const generate = async () => {
    if (!picked.template_id) {
      toast.error("Please select an order template");
      return;
    }
    if (templateSections.length === 0) {
      toast.error("Selected template has no sections — add sections in Knowledge Base or re-seed");
      return;
    }
    if (!picked.notes.trim()) {
      toast.error("Please enter drafting instructions before generating");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/drafts/generate`, {
        template_id: picked.template_id,
        selected_gr_ids: picked.gr_ids,
        selected_citation_ids: picked.citation_ids,
        selected_clause_ids: picked.clause_ids,
        generation_mode: picked.mode,
        additional_notes: picked.notes,
      });
      const requestId = data.draft_request_id;
      toast.info("Generation started — this can take 60-90s");

      // Poll for completion (max ~30 minutes)
      const start = Date.now();
      let status = data.generation_status;
      let draftId = data.draft_version_id;
      while (status === "Running" && Date.now() - start < 1800000) {
        await new Promise((r) => setTimeout(r, 3000));
        const poll = await api.get(`/drafts/by-request/${requestId}`);
        status = poll.data.generation_status;
        draftId = poll.data.draft_version_id;
        if (poll.data.error) throw new Error(poll.data.error);
      }
      if (status !== "Completed" || !draftId) {
        throw new Error(status === "Failed" ? "Generation failed" : "Generation timed out");
      }
      toast.success("Draft generated");
      navigate(`/cases/${caseId}/review/${draftId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (!caseId) {
    return (
      <Layout breadcrumbs={["SSRD", "Draft Generator"]}>
        <h1 className="text-2xl font-bold mb-4">Pick a case to draft</h1>
        <Card><CardContent className="p-0"><Table>
          <TableHeader><TableRow><TableHead>Case</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {cases.map((c) => (
              <TableRow key={c.case_id}>
                <TableCell className="font-mono text-xs">{c.case_number}/{c.case_year}</TableCell>
                <TableCell className="text-xs">{c.case_status}</TableCell>
                <TableCell><Button size="sm" onClick={() => setCaseId(c.case_id)}>Open</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></CardContent></Card>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[
      { label: "SSRD", to: "/dashboard" },
      { label: "Cases", to: "/cases" },
      bundle?.case ? { label: `${bundle.case.case_number}/${bundle.case.case_year}`, to: `/cases/${bundle.case.case_id}` } : "…",
      "Draft",
    ]}>
      <CaseContextHeader caseDoc={bundle?.case} />

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
        <Card data-testid="draft-context-card"><CardContent className="p-5 space-y-4">
          <div>
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase">A. Draft Context</div>
            <h2 className="text-lg font-semibold">Case Summary</h2>
          </div>
          {bundle && (
            <div className="space-y-2 text-sm">
              <Row label="Subject" value={bundle.case.subject_title} />
              <Row label="Applicant" value={bundle.parties.filter((p) => ["Applicant","Appellant"].includes(p.party_role)).map((p) => p.party_name).join(", ") || "—"} />
              <Row label="Respondent" value={bundle.parties.filter((p) => ["Respondent","Opponent"].includes(p.party_role)).map((p) => p.party_name).join(", ") || "—"} />
              <Row label="Property" value={bundle.properties.map((p) => `Survey ${p.survey_number}, ${p.village}`).join("; ") || "—"} />
              <Row label="Lower Order" value={bundle.lower_orders.map((o) => `${o.authority_name} ${o.order_number} (${o.order_date}) — ${o.outcome_of_lower_order}`).join("; ") || "—"} />
              <Row label="Documents" value={`${bundle.documents.length} uploaded · ${bundle.documents.filter((d) => d.processing_status === "Completed").length} processed`} />
            </div>
          )}
          <div className="pt-2 border-t border-border">
            <Label className="text-xs" htmlFor="draft-instructions">
            SSRD Officer Instructions Notes<span className="text-red-600">*</span>
            </Label>
            <Textarea
              id="draft-instructions"
              rows={3}
              required
              placeholder="Enter specific instructions for this draft (e.g. emphasis, tone, points to include)…"
              value={picked.notes}
              onChange={(e) => setPicked({ ...picked, notes: e.target.value })}
              data-testid="draft-instructions-input"
            />
          </div>
        </CardContent></Card>

        <Card data-testid="draft-inputs-card"><CardContent className="p-5 space-y-5">
          <div>
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase">B. Inputs</div>
            <h2 className="text-lg font-semibold">Drafting Inputs</h2>
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1">
              <FileText className="size-3.5" /> Order Template <span className="text-red-600">*</span>
            </Label>
            <Select value={picked.template_id} onValueChange={(v) => setPicked({ ...picked, template_id: v })}>
              <SelectTrigger data-testid="draft-template-select"><SelectValue placeholder="Select template…" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.template_name} (v{t.version_no})</SelectItem>)}
              </SelectContent>
            </Select>
            {picked.template_id && templateSections.length > 0 && (
              <div className="mt-2 rounded-md border border-border bg-secondary/20 p-2" data-testid="template-sections-preview">
                <div className="text-[10px] tracking-widest text-muted-foreground uppercase mb-1.5">
                  Order sections ({templateSections.length})
                </div>
                <ol className="text-xs space-y-0.5 list-decimal list-inside gujarati">
                  {templateSections.map((s) => (
                    <li key={s.section_id || s.section_key}>{s.section_name}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Mode</Label>
            <Select value={picked.mode} onValueChange={(v) => setPicked({ ...picked, mode: v })}>
              <SelectTrigger data-testid="draft-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="rounded-md border border-border bg-secondary/30 p-3">
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Engine</div>
            <div className="text-sm font-semibold">{ENGINE_LABEL}</div>
            <div className="text-[11px] text-muted-foreground font-mono">{DISPLAY_MODEL}</div>
          </div>

          <ReferencePickList
            title="GR References"
            icon={BookOpen}
            items={grs}
            idKey="gr_id"
            getLabel={(g) => g.gr_number}
            getSecondary={(g) => g.subject}
            getSearchText={(g) => [g.gr_number, g.subject, g.summary].filter(Boolean).join(" ")}
            searchPlaceholder="Search by GR number or subject…"
            checked={picked.gr_ids}
            onToggle={(id) => toggleArr("gr_ids", id)}
            testIdPrefix="draft-gr"
          />
          <ReferencePickList
            title="Legal Citations"
            icon={BookOpen}
            items={citations}
            idKey="citation_id"
            getLabel={(c) => c.citation_title}
            getSecondary={(c) => {
              const parts = [c.citation_number, c.legal_topic].filter(Boolean);
              return parts.length ? parts.join(" · ") : undefined;
            }}
            getSearchText={(c) => [c.citation_title, c.citation_number, c.legal_topic, c.summary].filter(Boolean).join(" ")}
            searchPlaceholder="Search by title, section, or topic…"
            checked={picked.citation_ids}
            onToggle={(id) => toggleArr("citation_ids", id)}
            testIdPrefix="draft-citation"
          />
          <ReferencePickList
            title="Reusable Clauses"
            icon={Hammer}
            items={clauses}
            idKey="clause_id"
            getLabel={(c) => c.clause_title}
            getSecondary={(c) => {
              const cat = c.clause_category || "";
              const st = c.approval_status || "";
              return [cat, st].filter(Boolean).join(" · ") || undefined;
            }}
            getSearchText={(c) => [c.clause_title, c.clause_category, c.clause_text_gujarati, c.usage_condition].filter(Boolean).join(" ")}
            searchPlaceholder="Search by clause title or category…"
            checked={picked.clause_ids}
            onToggle={(id) => toggleArr("clause_ids", id)}
            testIdPrefix="draft-clause"
          />
        </CardContent></Card>
      </div>

      <Card className="mt-6"><CardContent className="p-5">
        <div className="text-[10px] tracking-widest text-muted-foreground uppercase mb-2">C. Prompt / Instruction Preview (read-only)</div>
        <div className="text-sm bg-secondary/50 border border-border rounded-md p-3 leading-relaxed" data-testid="prompt-preview">
          Generate an editable Gujarati SSRD order draft based only on verified
          extracted facts, the selected template, selected references, and
          uploaded case documents. Do not make any final legal decision. Keep
          the final decision editable by the officer. Output language: Gujarati.
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <span className="kbd">{ENGINE_LABEL}</span>
          <Button
            disabled={generating || !picked.notes.trim() || !picked.template_id || templateSections.length === 0}
            onClick={generate}
            className="gap-2"
            data-testid="generate-draft-btn"
          >
            {generating ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            {generating ? "Generating…" : "Generate Draft Order"}
          </Button>
        </div>
      </CardContent></Card>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-3 text-sm">
      <div className="w-28 text-[10px] tracking-widest text-muted-foreground uppercase pt-0.5">{label}</div>
      <div className="flex-1 text-foreground">{value}</div>
    </div>
  );
}

